import { getEnrolledStudentsStats, getEnrolledStudentsReports } from '@/app/services/reports/reports';
import * as Actions from '@/app/store/actions';
import { Avatar } from '@material-ui/core';
import moment from 'moment-timezone';
import Pusher from 'pusher-js';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import secureLocalStorage from 'react-secure-storage';
import { useReactToPrint } from 'react-to-print';
import AppliedFiltersBar from '@shared/Reports/AppliedFiltersBar';
import ReportsHeader from '@shared/Reports/ReportsHeader';
import StatBox from '@shared/Reports/StatBox';
import TableTooltip from '@shared/Reports/TableTooltip';
import EnrolledStudentsFiltersBar from './EnrolledStudentsFiltersBar';
import EnrolledStudentsPrintViewTable from './EnrolledStudentsPrintTable';
import EnrolledStudentsTable from './EnrolledStudentsTable';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

const EnrolledStudentsReports = () => {
	const user = useSelector((state) => state.auth.user);
	const [filters, setFilters] = useState({
		page: 1,
		start_date: null,
		end_date: null,
		student_id: [],
		parent_id: [],
		room_id: [],
		status: { label: 'Active', value: true },
		export: null,
		sort: null,
		dir: 'desc',
		timezone: user.data.school.timezone || null,
	});
	const viewAsId = secureLocalStorage.getItem('view_as_id');
	const authToken = secureLocalStorage.getItem('jwt_access_token');
	const [loading, setLoading] = useState(false);
	const [printData, setPrintData] = useState([]);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [printDataLoading, setPrintDataLoading] = useState(false);
	const printRef = useRef();
	const navigate = useNavigate();
	const [statsLoading, setStatsLoading] = useState(false);
	const [rows, setRows] = useState([]);
	const [totalResults, setTotalResults] = useState(0);
	const dispatch = useDispatch();
	const cache = useRef({}); // Cache object to prevent refetching pages already fetched
	const prevFilters = useRef(filters); // Track previous filters to know when to refetch a set of results
	const [statsData, setStatsData] = useState({});
	const [exportType, setExportType] = useState(null);
	const [exportLoading, setExportLoading] = useState(false);
	const exportTimeoutRef = useRef(null);

	useEffect(() => {
		const pusher = new Pusher(import.meta.env.VITE_PUSHER_CHANNEL_ID, {
			cluster: import.meta.env.VITE_PUSHER_CLUSTER_ID,
			channelAuthorization: {
				headers: {
					Authorization: `Bearer ${authToken}`,
				},
				endpoint: `${import.meta.env.VITE_API_ENDPOINT}broadcasting/auth`,
			},
		});
		const channel = pusher.subscribe(
			`private-App.Models.User.${user.role[0] === 'super_school_admin' ? viewAsId : user.data?.id}`
		);

		channel.bind('report.export.complete', (res) => {
			const exportLink = JSON.parse(window.localStorage.getItem('export_uuid'));
			if (exportLink && exportLink === res.uuid) {
				setExportLoading(false);
				if (exportTimeoutRef.current) {
					clearTimeout(exportTimeoutRef.current);
				}
				window.localStorage.removeItem('export_uuid');

				const anchor = document.createElement('a');
				anchor.href = res.link;
				anchor.download = res.filename;
				anchor.click();
				anchor.remove();
			}
		});

		return () => {
			pusher.disconnect();
		};
	}, []);

	const handlePrint = useReactToPrint({
		content: () => printRef.current,
	});

	useEffect(() => {
		if (user.data.school.timezone !== filters.timezone) {
			setFilters({
				...filters,
				timezone: user.data.school.timezone,
			});
		}
	}, [user]);

	const calculatePercentChange = (current, previous) => {
		if (previous === 0) {
			return current * 100;
		}
		return (((current - previous) / previous) * 100).toFixed(1);
	};

	const generateQueryParams = (filters, exportType = null) => {
		return {
			page: filters.page,
			start_date: filters.start_date,
			end_date: filters.end_date,
			student_id: filters.student_id?.length > 0 ? filters.student_id.map((item) => item.value) : null,
			parent_id: filters.parent_id?.length > 0 ? filters.parent_id.map((item) => item.value) : null,
			room_id: filters.room_id?.length > 0 ? filters.room_id.map((item) => item.value) : null,
			status: filters.status?.value === true ? 1 : filters.status?.value === false ? 0 : null,
			export: exportType,
			sort: filters.sort,
			dir: filters.dir,
		};
	};

	const formatDuration = (days) => {
		if (days < 365) {
			return `${days} days`;
		} else {
			const years = Math.floor(days / 365);
			const remainingDays = days % 365;
			return `${years} years ${remainingDays} days`;
		}
	};

	// Helper function to update filters and reset page to 1 if any filter other than page changes
	const updateFilters = (newFilters) => {
		const filtersChanged = Object.keys(newFilters).some((key) => key !== 'page' && newFilters[key] !== filters[key]);
		if (filtersChanged) {
			setFilters({ ...newFilters, page: 1 });
		} else {
			setFilters(newFilters);
		}
	};

	// Helper function to generate cache key based on applied filters. Prevents unnecessary refetching of results
	const generateCacheKey = (params) => {
		return Object.entries(params)
			.filter(([key]) => key !== 'export')
			.map(([key, value]) => `${key}:${JSON.stringify(value)}`)
			.join('|');
	};

	useEffect(() => {
		// Clear cache if any filter other than page changes
		const filtersChanged = Object.keys(filters).some(
			(key) => key !== 'page' && filters[key] !== prevFilters.current[key]
		);
		if (filtersChanged) {
			cache.current = {};
		}
		prevFilters.current = filters;

		setLoading(true);
		const queryParams = generateQueryParams(filters);
		const cacheKey = generateCacheKey(queryParams);

		// Check if the results for the current page are already in the cache
		if (cache.current[cacheKey]) {
			const cachedData = cache.current[cacheKey];
			setRows(cachedData.data);
			setTotalResults(cachedData.total);
			setLoading(false);

			// Only fetch stats if non-page filters changed
			if (filtersChanged) {
				fetchStatsData(queryParams);
			}
		} else {
			getEnrolledStudentsReports(queryParams)
				.then((res) => {
					setRows(res.data.data);
					setTotalResults(res.data.meta.total);
					// Store the results in the cache
					cache.current[cacheKey] = {
						data: res.data.data,
						total: res.data.meta.total,
					};

					// Only fetch stats if non-page filters changed
					if (filtersChanged) {
						fetchStatsData(queryParams);
					}
				})
				.catch(() => {
					dispatch(
						Actions.showMessage({
							message: 'Failed to fetch report',
							variant: 'error',
						})
					);
					setRows([]);
					setTotalResults(0);
					setStatsData({});
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, [filters]);

	// perform initial stats fetch
	useEffect(() => {
		const queryParams = generateQueryParams(filters);
		fetchStatsData(queryParams);
	}, []);

	const fetchStatsData = async (queryParams) => {
		setStatsLoading(true);
		try {
			const res = await getEnrolledStudentsStats(queryParams);
			const stats = res.data;

			const formatDateRange = (start, end) => {
				if (!start || !end) return null;
				const startDate = moment(start);
				const endDate = moment(end);
				if (startDate.month() === endDate.month()) {
					return `${startDate.format('MMM D')} - ${endDate.format('D')}`;
				} else {
					return `${startDate.format('MMM D')} - ${endDate.format('MMM D')}`;
				}
			};

			// Only include comparison data if date filters are applied
			const hasDateFilters = filters.start_date && filters.end_date;

			setStatsData({
				avg_enrollment_duration: {
					total: formatDuration(stats.avg_enrollment_duration),
					percentChange: hasDateFilters
						? calculatePercentChange(+stats.avg_enrollment_duration, +stats.previous?.avg_enrollment_duration || 0)
						: null,
					dateRange: hasDateFilters ? formatDateRange(stats.previous?.start_date, stats.previous?.end_date) : null,
				},
				enrollment_rate: {
					total: stats.enrollment_rate + '%',
					percentChange: hasDateFilters
						? calculatePercentChange(+stats.enrollment_rate, +stats.previous?.enrollment_rate || 0)
						: null,
					dateRange: hasDateFilters ? formatDateRange(stats.previous?.start_date, stats.previous?.end_date) : null,
				},
				retention_rate: {
					total: stats.retention_rate + '%',
					percentChange: hasDateFilters
						? calculatePercentChange(+stats.retention_rate, +stats.previous?.retention_rate || 0)
						: null,
					dateRange: hasDateFilters ? formatDateRange(stats.previous?.start_date, stats.previous?.end_date) : null,
				},
				active_families: {
					total: stats.active_families,
					percentChange: hasDateFilters
						? calculatePercentChange(+stats.active_families, +stats.previous?.active_families || 0)
						: null,
					dateRange: hasDateFilters ? formatDateRange(stats.previous?.start_date, stats.previous?.end_date) : null,
				},
			});
		} catch {
			dispatch(
				Actions.showMessage({
					message: 'Failed to fetch statistics data',
					variant: 'error',
				})
			);
			setStatsData({});
		} finally {
			setStatsLoading(false);
		}
	};

	const handleExport = async (type) => {
		const queryParams = generateQueryParams(filters, type);
		try {
			setExportLoading(true);
			queryParams.uuid = uuidv4();
			window.localStorage.setItem('export_uuid', JSON.stringify(queryParams.uuid));
			await getEnrolledStudentsReports(queryParams);

			// Set a timeout to reset exportLoading if no response within 20 seconds
			exportTimeoutRef.current = setTimeout(() => {
				if (!exportLoading) return;
				setExportLoading(false);
				dispatch(
					Actions.showMessage({
						message: 'Export failed to generate. Please try again.',
						variant: 'error',
					})
				);
			}, 20000);
		} catch {
			dispatch(
				Actions.showMessage({
					message: 'Failed to export report',
					variant: 'error',
				})
			);
		}
	};

	const handlePrintClick = async () => {
		setExportType('print');
		setPrintDataLoading(true);
		try {
			const queryParams = generateQueryParams(filters, 'print');

			const res = await getEnrolledStudentsReports(queryParams);
			setPrintData(res.data.data);
		} catch {
			dispatch(
				Actions.showMessage({
					message: 'Failed to print report',
					variant: 'error',
				})
			);
		} finally {
			setPrintDataLoading(false);
		}
	};

	useEffect(() => {
		if (!printDataLoading && printData.length > 0) {
			handlePrint();
		}
	}, [printDataLoading, printData]);

	const columns = [
		{
			id: 'student',
			label: 'Student',
			render: (row) => {
				return (
					<div className="flex items-center gap-16">
						<Avatar src={row.thumb} />
						<TableTooltip>
							<button
								className="hover:underline cursor-pointer font-bold truncate"
								onClick={() => {
									setFilters({
										...filters,
										page: 1,
										student_id: [
											{
												label: `${row.first_name} ${row.last_name}`,
												value: row.id,
											},
										],
									});
								}}
								type="button"
							>
								{`${row.last_name}, ${row.first_name}`}
							</button>
						</TableTooltip>
					</div>
				);
			},
			printRender: (row) => {
				return `${row.last_name}, ${row.first_name}`;
			},
			widthClass: 'min-w-256',
		},
		{
			id: 'parent',
			widthClass: 'min-w-200',
			label: 'Parent/Legal Guardian',
			render: (row) => (
				<div className="flex flex-col gap-16">
					{row?.parents.map((parent) => {
						return (
							<div className="flex items-center gap-16" key={parent.id}>
								<Avatar src={parent.thumb} />
								<div className="flex flex-col truncate">
									<TableTooltip>
										<span className="font-bold truncate">
											{parent.first_name} {parent.last_name}
										</span>
									</TableTooltip>
									<span>{parent.relation_with_child || 'Contact'}</span>
								</div>
							</div>
						);
					})}
				</div>
			),
			printRender: (row) => (
				<div className="flex flex-col gap-16">
					{row?.parents.map((parent) => (
						<div className="flex items-center gap-16" key={parent.id}>
							<span>
								{parent.first_name} {parent.last_name}
							</span>
						</div>
					))}
				</div>
			),
		},

		{
			id: 'room',
			label: 'Room',
			render: (row) => (
				<div className="flex items-center gap-16">
					<Avatar src={row.room.thumb} />
					<button
						className="hover:underline cursor-pointer"
						onClick={() =>
							setFilters({
								...filters,
								page: 1,
								room_id: [{ label: row.room.name, value: row.room.id }],
							})
						}
						type="button"
					>
						{row.room.name}
					</button>
				</div>
			),
			printRender: (row) => row.room.name,
			widthClass: 'min-w-200',
		},
		{
			id: 'phone',
			widthClass: 'min-w-200',
			label: 'Phone Number',
			render: (row) => (
				<div className="flex flex-col gap-16 h-full justify-between">
					{row?.parents.map((parent) => (
						<div className="flex items-center gap-16" key={parent.id}>
							<TableTooltip>
								<span className="truncate">{parent.phone}</span>
							</TableTooltip>
							<Avatar className="invisible w-0" />
						</div>
					))}
				</div>
			),
			printRender: (row) => (
				<div className="flex flex-col gap-16">
					{row?.parents.map((parent) => (
						<div className="flex items-center gap-16" key={parent.id}>
							<span>{parent.phone}</span>
						</div>
					))}
				</div>
			),
		},
		{
			id: 'email',
			widthClass: 'min-w-200',
			label: 'Email',
			render: (row) => (
				<div className="flex flex-col gap-16">
					{row?.parents.map((parent) => (
						<div className="flex items-center gap-16" key={parent.id}>
							<TableTooltip>
								<span className="truncate">{parent.email}</span>
							</TableTooltip>
							<Avatar className="invisible w-0" />
						</div>
					))}
				</div>
			),
			printRender: (row) => (
				<div className="flex flex-col gap-16">
					{row?.parents.map((parent) => (
						<div className="flex items-center gap-16 break" key={parent.id}>
							<span>{parent.email}</span>
						</div>
					))}
				</div>
			),
		},
		{
			id: 'enrollment_date',
			label: 'Enrollment Date',
			render: (row) => (
				<div className="flex h-full">
					<button
						className=" py-[11px]"
						onClick={() =>
							setFilters({
								...filters,
								page: 1,
								start_date: moment(row.datetime).format('YYYY-MM-DD'),
								end_date: moment(row.datetime).format('YYYY-MM-DD'),
							})
						}
						type="button"
					>
						{moment(row.enrollment_date, 'MM/DD/YYYY').format('MM/DD/YY')}
					</button>
				</div>
			),
			printRender: (row) => (
				<div className="w-full text-right">{moment(row.enrollment_date, 'MM/DD/YYYY').format('MM/DD/YY')}</div>
			),
			widthClass: '',
		},
		{
			id: 'withdrawal_date',
			label: 'Withdrawal Date',
			render: (row) => (
				<button
					className="py-[11px] inline-block"
					onClick={() =>
						setFilters({
							...filters,
							page: 1,
							start_date: moment(row.datetime).format('YYYY-MM-DD'),
							end_date: moment(row.datetime).format('YYYY-MM-DD'),
						})
					}
					type="button"
				>
					{row.withdrawal_date ? moment(row.withdrawal_date).format('MM/DD/YY') : <>&mdash;</>}
				</button>
			),
			printRender: (row) => (row.withdrawal_date ? moment(row.withdrawal_date).format('MM/DD/YY') : <>&mdash;</>),
			widthClass: '',
		},
	];
	return (
		<div>
			<ReportsHeader
				exportLoading={exportLoading}
				exportsDisabled={!rows?.length}
				onBack={() => navigate(-1)}
				onDownloadPDF={() => handleExport('pdf')}
				onExport={handleExport}
				onPrint={handlePrintClick}
				pdfLoading={false}
				printLoading={printDataLoading}
				subtitle="Track the enrollment date and withdrawal date of each student. You can search for students by room or program."
				title="Enrolled Students Report"
			/>
			<EnrolledStudentsFiltersBar filters={filters} setFilters={updateFilters} />
			<AppliedFiltersBar filters={filters} setFilters={updateFilters} />
			<div className="bg-white">
				{Object.keys(statsData).length > 0 || totalResults > 0 ? (
					<div className="px-32 py-48">
						<div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-16">
							<StatBox
								data={{ total: totalResults }}
								loading={statsLoading || loading}
								title={
									filters.start_date && filters.end_date
										? 'Total New Enrolled Students'
										: 'Total Active Enrolled Students'
								}
								type="totalEnrollment"
							/>
							{Object.keys(statsData).length > 0 ? (
								<>
									<StatBox
										data={statsData.avg_enrollment_duration}
										loading={statsLoading || loading}
										title="Avg. Enrollment Duration"
										type="totalStudent"
									/>
									<StatBox
										data={statsData.enrollment_rate}
										loading={statsLoading || loading}
										title="Enrollment Rate"
										type="dailyStudent"
									/>
									<StatBox
										data={statsData.retention_rate}
										loading={statsLoading || loading}
										title="Retention Rate"
										type="dailyTime"
									/>
									<StatBox
										data={statsData.active_families}
										loading={statsLoading || loading}
										title="Active Families"
										type=""
									/>
								</>
							) : null}
						</div>
					</div>
				) : null}
				<div>
					<EnrolledStudentsTable
						columns={columns}
						data={rows}
						dir={filters.dir}
						filters={filters}
						loading={loading}
						page={filters.page}
						rowsPerPage={rowsPerPage}
						setDir={(dir) => updateFilters({ ...filters, dir })}
						setFilters={updateFilters}
						setPage={(page) => updateFilters({ ...filters, page })}
						setRowsPerPage={setRowsPerPage}
						setSort={(sort) => updateFilters({ ...filters, sort })}
						sort={filters.sort}
						totalResults={totalResults}
					/>
				</div>
				<div className="h-0 overflow-hidden">
					<EnrolledStudentsPrintViewTable
						columns={columns}
						data={printData}
						exportType={exportType}
						filters={filters}
						ref={printRef}
						title="Enrolled Students Report"
						user={user}
					/>
				</div>
			</div>
		</div>
	);
};

export default EnrolledStudentsReports;
